import { useState } from "react";
import { Page } from "../App";
import Map from "../components/Map";
import { AIRPORT_TYPES, COLORS, TIME_OPTIONS } from "../config/carto";
import "./AirportAccess.css";

interface Props {
  navigate: (p: Page) => void;
}

export default function AirportAccess({ navigate }: Props) {
  const [time, setTime] = useState(90);
  const [airportStatus, setAirportStatus] = useState<string[]>(AIRPORT_TYPES);
  const [count, setCount] = useState(0);
  const [totalPop, setTotalPop] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredStat, setHoveredStat] = useState<string | null>(null);

  const toggleStatus = (s: string) =>
    setAirportStatus((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const coverage = totalPop > 0 ? ((totalPop / 1440000000) * 100).toFixed(1) : null;

  const STATS = [
    {
      id: "airports",
      val: loading ? "—" : count,
      label: "Airports",
      sub: "in selection",
      color: "#009A84",
      bg: "rgba(0,154,132,0.1)",
      border: "rgba(0,154,132,0.2)",
      glow: "rgba(0,154,132,0.3)",
      neo: false,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" fill="#009A84" />
        </svg>
      ),
    },
    ...(coverage
      ? [
          {
            id: "coverage",
            val: `${coverage}%`,
            label: "Coverage",
            sub: "of India's population",
            color: "#EB280F",
            bg: "rgba(235,40,15,0.08)",
            border: "rgba(235,40,15,0.2)",
            glow: "rgba(235,40,15,0.25)",
            neo: false,
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EB280F" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
              </svg>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="db">
      <Map
        time={time}
        airportStatus={airportStatus}
        showPop={false}
        setCount={setCount}
        setTotalPop={setTotalPop}
        setLoading={setLoading}
        setError={setError}
      />

      <div className="db-topbar">
        <div style={{ width: 120 }} />
        <div className="db-topbar-center">
          <div className="db-topbar-dot" />
        </div>
        <div className="db-topbar-right">
          {loading && (
            <div className="db-loading-pill">
              <div className="db-loading-dot" />
              Loading
            </div>
          )}
          {error && <div className="db-error-pill">{error}</div>}
        </div>
      </div>

      <div className="db-stats">
        {STATS.map((stat) => (
          <div
            key={stat.id}
            className="db-stat-card"
            onMouseEnter={() => setHoveredStat(stat.id)}
            onMouseLeave={() => setHoveredStat(null)}
            style={{
              background: stat.bg,
              borderColor: stat.border.replace(/[\d.]+\)$/, "0.55)"),
              boxShadow: hoveredStat === stat.id ? `0 12px 36px ${stat.glow}` : `0 8px 28px ${stat.glow}`,
              transform: hoveredStat === stat.id ? "translateY(-2px)" : "none",
            }}
          >
            <div className="db-stat-icon" style={{ background: stat.bg, border: `1px solid ${stat.border}` }}>
              {stat.icon}
            </div>
            <div>
              <div className="db-stat-val" style={{ color: stat.color }}>{stat.val}</div>
              <div className="db-stat-label">{stat.label}</div>
              <div className="db-stat-sub">{stat.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="db-timebar">
        <div className="db-timebar-label">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Drive time
        </div>
        <div className="db-timebar-pills">
          {TIME_OPTIONS.map((t) => (
            <button key={t} onClick={() => setTime(t)} className={`db-time-pill ${time === t ? "active" : ""}`}>
              {t}
              <span>min</span>
            </button>
          ))}
        </div>
      </div>

      <div className="db-legend">
        <div className="db-legend-title">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Airport status
        </div>
        {AIRPORT_TYPES.map((s) => {
          const isActive = airportStatus.includes(s);
          const col = COLORS[s] || "#fff";
          return (
            <div key={s} className={`db-legend-item ${isActive ? "active" : ""}`} onClick={() => toggleStatus(s)}>
              <div
                className="db-legend-dot"
                style={{ background: isActive ? col : "transparent", borderColor: col, boxShadow: isActive ? `0 0 10px ${col}90` : "none" }}
              />
              <span style={{ color: isActive ? "white" : "rgba(255,255,255,0.25)" }}>{s}</span>
              <div
                className="db-legend-check"
                style={{ background: isActive ? col : "transparent", borderColor: isActive ? col : "rgba(255,255,255,0.1)" }}
              >
                {isActive && (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}